import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Envelope, PaperPlaneTilt, Eye, X, Trash, CheckCircle, Clock, XCircle } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api`, withCredentials: true });

const STATUS_LABEL = {
  open:      { text: "🟡 Offen",       cls: "bg-amber-100 text-amber-800 border-amber-200" },
  offered:   { text: "🔵 Angebot",     cls: "bg-blue-100 text-blue-800 border-blue-200" },
  confirmed: { text: "🟢 Bestätigt",    cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  declined:  { text: "🔴 Abgelehnt",    cls: "bg-rose-100 text-rose-800 border-rose-200" },
  closed:    { text: "⚪ Geschlossen",  cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function Inquiries() {
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [offer, setOffer] = useState({ room_names: [], total_price: "", deposit: "", custom_note: "", lang: "" });
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  // Reservierungsbestätigung
  const [confirmMode, setConfirmMode] = useState(false);
  const [items, setItems] = useState([{ room_name: "", nights: 0, total: "" }]);
  const [conf, setConf] = useState({
    deposit: "", deposit_due: "", balance_due: "",
    arrival_time: "15:00", departure_time: "10:00", note: "",
  });
  const [depositTouched, setDepositTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    const q = statusFilter ? `?status=${statusFilter}` : "";
    const r = await api.get(`/inquiries${q}`);
    setRows(r.data || []);
  };
  useEffect(() => { load(); api.get("/rooms").then(r => setRooms(r.data || [])).catch(()=>{}); }, [statusFilter]);

  const openDetail = (row) => {
    setSelected(row);
    setConfirmMode(false);
    const prefill = row.accommodation && rooms.find(r => r.name === row.accommodation) ? [row.accommodation] : [];
    setOffer({
      room_names: prefill,
      total_price: "", deposit: "", custom_note: "", lang: row.lang || "de",
    });
    // Reservierungs-Defaults
    const defaultRoom = row.accommodation && rooms.find(r => r.name === row.accommodation) ? row.accommodation : "";
    setItems([{ room_name: defaultRoom, nights: row.nights || 0, total: "" }]);
    const today = new Date();
    const depDue = new Date(today); depDue.setDate(today.getDate() + 3);
    const ci = row.check_in ? new Date(row.check_in) : new Date();
    const balDue = new Date(ci); balDue.setDate(ci.getDate() - 7);
    const iso = d => d.toISOString().slice(0,10);
    setConf({
      deposit: "", deposit_due: iso(depDue), balance_due: iso(balDue),
      arrival_time: "15:00", departure_time: "10:00", note: "",
    });
    setDepositTouched(false);
  };

  // Auto-Anzahlung 50 % (überschreibbar) — bleibt mit Summe synchron, bis der Admin manuell editiert
  const itemsSum = items.reduce((s, it) => s + (parseFloat(it.total) || 0), 0);
  useEffect(() => {
    if (!confirmMode || depositTouched) return;
    if (itemsSum > 0) {
      setConf(c => ({ ...c, deposit: (itemsSum / 2).toFixed(2) }));
    }
  }, [itemsSum, confirmMode, depositTouched]);

  const balance = Math.max(0, itemsSum - (parseFloat(conf.deposit) || 0));

  const addItem = () => setItems([...items, { room_name: "", nights: selected?.nights || 0, total: "" }]);
  const rmItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const previewPdf = async () => {
    if (items.some(it => !it.room_name || !it.total)) return toast.error("Bitte alle Zimmer-Positionen ausfüllen");
    try {
      const r = await api.post(`/inquiries/${selected.id}/preview-reservation-pdf`, {
        items: items.map(it => ({ room_name: it.room_name, nights: parseInt(it.nights)||0, total: parseFloat(it.total) })),
        deposit: parseFloat(conf.deposit) || 0,
        deposit_due: conf.deposit_due, balance_due: conf.balance_due,
        arrival_time: conf.arrival_time, departure_time: conf.departure_time,
        note: conf.note,
      }, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
  };

  const confirmReservation = async () => {
    if (items.some(it => !it.room_name || !it.total)) return toast.error("Bitte alle Zimmer-Positionen ausfüllen");
    if (!conf.deposit_due || !conf.balance_due) return toast.error("Fälligkeitsdaten fehlen");
    if (!window.confirm(`Reservierung bestätigen und Bestätigungs-Mail + PDF an ${selected.email} senden?`)) return;
    setConfirming(true);
    try {
      const r = await api.post(`/inquiries/${selected.id}/confirm-reservation`, {
        items: items.map(it => ({ room_name: it.room_name, nights: parseInt(it.nights)||0, total: parseFloat(it.total) })),
        deposit: parseFloat(conf.deposit) || 0,
        deposit_due: conf.deposit_due, balance_due: conf.balance_due,
        arrival_time: conf.arrival_time, departure_time: conf.departure_time,
        note: conf.note,
      });
      toast.success(`Reservierung ${r.data.res_number} bestätigt · Mail + PDF versendet`);
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bestätigung fehlgeschlagen"); }
    finally { setConfirming(false); }
  };

  const sendOffer = async () => {
    if (!offer.room_names?.length || !offer.total_price) return toast.error("Mindestens ein Zimmer und Preis erforderlich");
    setSending(true);
    try {
      await api.post(`/inquiries/${selected.id}/send-offer-email`, {
        room_names: offer.room_names,
        room_name: offer.room_names.join(" + "),   // Backwards-compat für Alt-DB
        total_price: parseFloat(offer.total_price),
        deposit: parseFloat(offer.deposit || 0),
        custom_note: offer.custom_note,
        lang: offer.lang || null,
      });
      toast.success("Angebot per E-Mail versendet");
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSending(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/inquiries/${id}`, { status }); toast.success("Status aktualisiert"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Anfrage wirklich löschen?")) return;
    try { await api.delete(`/inquiries/${id}`); toast.success("Gelöscht"); load(); setSelected(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const preview = async () => {
    if (!offer.room_names?.length || !offer.total_price) return toast.error("Mindestens ein Zimmer und Preis erforderlich");
    const p = new URLSearchParams({
      room_names_csv: offer.room_names.join("|"),
      total_price: offer.total_price,
    });
    if (offer.lang) p.set("lang", offer.lang);
    const r = await api.get(`/inquiries/${selected.id}/preview-offer-email?${p}`);
    const w = window.open("", "_blank");
    w.document.write(r.data.html);
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{fontFamily:"'Work Sans',sans-serif"}}>📬 Reservierungsanfragen</h1>
          <p className="text-sm text-muted-foreground mt-1">Alle Anfragen aus dem Homepage-Formular · Angebot per Klick senden</p>
        </div>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white" data-testid="inq-status-filter">
          <option value="">Alle Anfragen</option>
          <option value="open">Offen</option>
          <option value="offered">Angebot gesendet</option>
          <option value="confirmed">Bestätigt</option>
          <option value="declined">Abgelehnt</option>
          <option value="closed">Geschlossen</option>
        </select>
      </div>

      <div className="msb-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase tracking-widest text-muted-foreground">
              <th className="text-left p-3">Eingang</th>
              <th className="text-left p-3">Gast</th>
              <th className="text-left p-3">Zeitraum</th>
              <th className="text-center p-3">Personen</th>
              <th className="text-left p-3">Wunsch</th>
              <th className="text-center p-3">Status</th>
              <th className="text-right p-3">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Keine Anfragen.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={()=>openDetail(r)} data-testid={`inq-row-${r.id}`}>
                <td className="p-3 whitespace-nowrap text-xs font-mono text-slate-500">{r.created_at?.slice(0,16).replace("T"," ")}</td>
                <td className="p-3">
                  <div className="font-semibold">{r.first_name} {r.last_name}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </td>
                <td className="p-3 whitespace-nowrap text-xs">{r.check_in} → {r.check_out} ({r.nights}N)</td>
                <td className="p-3 text-center text-xs">{r.adults} Erw.{r.children ? ` + ${r.children} Kd.` : ""}</td>
                <td className="p-3 text-xs">{r.accommodation || "—"}</td>
                <td className="p-3 text-center">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_LABEL[r.status]?.cls || ""}`}>{STATUS_LABEL[r.status]?.text || r.status}</span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 mr-1.5" onClick={(e)=>{e.stopPropagation(); openDetail(r);}} data-testid={`inq-open-${r.id}`}>
                    Öffnen
                  </button>
                  <button
                    className="h-8 w-8 rounded-lg bg-white border border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700 inline-flex items-center justify-center"
                    title="Anfrage löschen"
                    onClick={(e)=>{e.stopPropagation(); del(r.id);}}
                    data-testid={`inq-delete-row-${r.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail-Dialog */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={()=>setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="inq-detail-dialog">
            <div className="flex items-center justify-between p-5 border-b bg-slate-50 sticky top-0 z-10">
              <div>
                <div className="text-xl font-bold">{selected.first_name} {selected.last_name}</div>
                <div className="text-xs text-slate-500">{selected.email} · {selected.phone || "kein Telefon"}</div>
              </div>
              <button onClick={()=>setSelected(null)} className="h-9 w-9 rounded-lg hover:bg-slate-200 flex items-center justify-center" data-testid="inq-close">
                <X size={20}/>
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Details */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Zeitraum:</span> <strong>{selected.check_in} → {selected.check_out}</strong> ({selected.nights} N.)</div>
                <div><span className="text-slate-500">Personen:</span> <strong>{selected.adults} Erw.{selected.children ? ` + ${selected.children} Kd.` : ""}</strong></div>
                <div><span className="text-slate-500">Zimmerwunsch:</span> <strong>{selected.accommodation || "—"}</strong></div>
                <div><span className="text-slate-500">Reisezweck:</span> <strong>{selected.purpose || "—"}</strong></div>
                <div><span className="text-slate-500">Verpflegung:</span> <strong>{(selected.meals || []).join(", ") || "—"}</strong></div>
                <div><span className="text-slate-500">Parkplatz:</span> <strong>{selected.parking || "—"}</strong></div>
                <div className="col-span-2"><span className="text-slate-500">Kinder-Alter:</span> {selected.children_ages || "—"}</div>
                <div className="col-span-2"><span className="text-slate-500">Erfahren über:</span> <strong>{selected.referral || "—"}</strong></div>
              </div>
              {selected.message && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <div className="text-xs font-semibold text-slate-500 mb-1">NACHRICHT</div>
                  <div className="whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}

              {/* Umschalter: Angebot vs. Reservierung */}
              <div className="flex gap-2 border-b pb-2">
                <button onClick={()=>setConfirmMode(false)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${!confirmMode?"bg-slate-900 text-white":"bg-white text-slate-600 border"}`} data-testid="tab-offer">💌 Angebot senden</button>
                <button onClick={()=>setConfirmMode(true)}  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${confirmMode?"bg-emerald-700 text-white":"bg-white text-slate-600 border"}`} data-testid="tab-confirm">✅ Reservierung bestätigen</button>
              </div>

              {!confirmMode && (
              /* Angebot senden */
              <div className="border-2 border-emerald-200 rounded-xl p-4 bg-emerald-50/50">
                <div className="font-bold text-emerald-900 mb-3">💌 Angebot per E-Mail senden</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Zimmer * <span className="font-normal text-slate-500">(Mehrfachauswahl möglich)</span></label>
                    <div className="flex flex-wrap gap-2" data-testid="offer-room-picker">
                      {rooms.map(r => {
                        const active = offer.room_names.includes(r.name);
                        return (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => setOffer({
                              ...offer,
                              room_names: active
                                ? offer.room_names.filter(n => n !== r.name)
                                : [...offer.room_names, r.name],
                            })}
                            className={`h-9 px-3 rounded-full text-xs font-semibold border transition ${
                              active
                                ? "bg-emerald-600 text-white border-emerald-700 shadow-sm"
                                : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"
                            }`}
                            data-testid={`offer-room-toggle-${r.name}`}
                          >
                            {active ? "✓ " : ""}{r.name}
                          </button>
                        );
                      })}
                    </div>
                    {offer.room_names.length > 0 && (
                      <div className="text-[11px] text-slate-500 mt-1.5">
                        Wird im Angebot angezeigt als: <strong className="text-slate-700">{offer.room_names.join(" + ")}</strong>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Gesamtpreis (€) *</label>
                    <input type="number" step="0.01" className="w-full h-10 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={offer.total_price} onChange={e=>setOffer({...offer, total_price: e.target.value})} data-testid="offer-price"/>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Sprache</label>
                    <select className="w-full h-10 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={offer.lang} onChange={e=>setOffer({...offer, lang: e.target.value})} data-testid="offer-lang">
                      <option value="de">Deutsch</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Anzahlung (€, optional)</label>
                    <input type="number" step="0.01" className="w-full h-10 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={offer.deposit} onChange={e=>setOffer({...offer, deposit: e.target.value})}/>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Zusätzliche Notiz (optional)</label>
                    <textarea rows="2" className="w-full rounded-lg border border-slate-300 px-2 py-2 bg-white text-sm" value={offer.custom_note} onChange={e=>setOffer({...offer, custom_note: e.target.value})} placeholder="Persönliche Notiz zum Angebot" data-testid="offer-note"/>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={preview} className="h-10 px-4 rounded-lg bg-white border border-slate-300 text-sm font-semibold hover:bg-slate-50 flex items-center gap-1" data-testid="offer-preview">
                    <Eye size={16}/> Vorschau
                  </button>
                  <button onClick={sendOffer} disabled={sending} className="flex-1 h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-1" data-testid="offer-send">
                    <PaperPlaneTilt size={16}/> {sending ? "…" : "Angebot senden"}
                  </button>
                </div>
              </div>
              )}

              {confirmMode && (
              /* Reservierung bestätigen */
              <div className="border-2 border-emerald-700/50 rounded-xl p-4 bg-emerald-50/40" data-testid="confirm-panel">
                <div className="font-bold text-emerald-900 mb-3">✅ Reservierung bestätigen & PDF an Gast senden</div>

                {/* Positionen (Multi-Zimmer) */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Zimmer-Positionen</div>
                    <button onClick={addItem} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800" data-testid="add-item">＋ Zimmer hinzufügen</button>
                  </div>
                  <div className="space-y-2">
                    {items.map((it, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`item-row-${i}`}>
                        <select className="col-span-6 h-9 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={it.room_name} onChange={e=>setItem(i, {room_name: e.target.value})} data-testid={`item-room-${i}`}>
                          <option value="">— Zimmer wählen —</option>
                          {rooms.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                        </select>
                        <input type="number" min="1" placeholder="Nächte" className="col-span-2 h-9 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={it.nights} onChange={e=>setItem(i, {nights: parseInt(e.target.value)||0})} data-testid={`item-nights-${i}`}/>
                        <input type="number" step="0.01" placeholder="Gesamt €" className="col-span-3 h-9 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={it.total} onChange={e=>setItem(i, {total: e.target.value})} data-testid={`item-total-${i}`}/>
                        <button onClick={()=>rmItem(i)} disabled={items.length===1} className="col-span-1 h-9 rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-30 flex items-center justify-center" title="Entfernen"><Trash size={16}/></button>
                      </div>
                    ))}
                  </div>
                  <div className="text-right text-sm mt-2">
                    <span className="text-slate-500">Gesamt: </span>
                    <span className="font-bold text-emerald-800" data-testid="items-sum">{itemsSum.toFixed(2).replace(".",",")} €</span>
                  </div>
                </div>

                {/* Anzahlung / Restbetrag */}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Anzahlung (€)</label>
                    <input type="number" step="0.01" className="w-full h-9 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={conf.deposit} onChange={e=>{setConf({...conf, deposit: e.target.value}); setDepositTouched(true);}} data-testid="conf-deposit"/>
                    <div className="text-[11px] text-slate-500 mt-1">Vorschlag 50 % · frei änderbar</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Restbetrag</label>
                    <div className="w-full h-9 rounded-lg border border-slate-200 px-2 bg-slate-50 text-sm flex items-center font-bold text-slate-700" data-testid="conf-balance">{balance.toFixed(2).replace(".",",")} €</div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Anzahlung fällig bis</label>
                    <input type="date" className="w-full h-9 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={conf.deposit_due} onChange={e=>setConf({...conf, deposit_due: e.target.value})} data-testid="conf-deposit-due"/>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Restbetrag fällig bis</label>
                    <input type="date" className="w-full h-9 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={conf.balance_due} onChange={e=>setConf({...conf, balance_due: e.target.value})} data-testid="conf-balance-due"/>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Anreise ab</label>
                    <input type="time" className="w-full h-9 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={conf.arrival_time} onChange={e=>setConf({...conf, arrival_time: e.target.value})}/>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Abreise bis</label>
                    <input type="time" className="w-full h-9 rounded-lg border border-slate-300 px-2 bg-white text-sm" value={conf.departure_time} onChange={e=>setConf({...conf, departure_time: e.target.value})}/>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Persönliche Notiz (optional, erscheint im PDF)</label>
                    <textarea rows="2" className="w-full rounded-lg border border-slate-300 px-2 py-1.5 bg-white text-sm" value={conf.note} onChange={e=>setConf({...conf, note: e.target.value})} placeholder="z.B. Kinderbett vorbereiten, Vegetarisches Frühstück, Zustellbett …" data-testid="conf-note"/>
                  </div>
                </div>

                <div className="flex gap-2 mt-3">
                  <button onClick={previewPdf} className="h-10 px-4 rounded-lg bg-white border border-slate-300 text-sm font-semibold hover:bg-slate-50 flex items-center gap-1" data-testid="conf-preview-pdf">
                    <Eye size={16}/> PDF-Vorschau
                  </button>
                  <button onClick={confirmReservation} disabled={confirming} className="flex-1 h-10 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-1" data-testid="conf-send">
                    <CheckCircle size={16}/> {confirming ? "…" : "Reservierung bestätigen & Mail+PDF senden"}
                  </button>
                </div>
              </div>
              )}

              {/* Status-Aktionen */}
              <div className="flex flex-wrap gap-2 pt-2">
                <button onClick={()=>setStatus(selected.id, "confirmed")} className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 flex items-center gap-1" data-testid="status-confirm">
                  <CheckCircle size={14}/> Als bestätigt markieren
                </button>
                <button onClick={()=>setStatus(selected.id, "declined")} className="h-9 px-3 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 flex items-center gap-1" data-testid="status-decline">
                  <XCircle size={14}/> Abgelehnt
                </button>
                <button onClick={()=>setStatus(selected.id, "closed")} className="h-9 px-3 rounded-lg bg-slate-500 text-white text-xs font-semibold hover:bg-slate-600 flex items-center gap-1">
                  <Clock size={14}/> Schließen
                </button>
                <button onClick={()=>del(selected.id)} className="h-9 px-3 rounded-lg bg-white border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-50 flex items-center gap-1 ml-auto" data-testid="inq-delete">
                  <Trash size={14}/> Löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
